"use client";

import * as React from "react";
import { Database, Shield, Activity, Webhook, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
const technicalFeatures = [{
  title: "Communication Bridging",
  description: "Eliminating linguistic and cultural barriers to ensure seamless project alignment across borders.",
  icon: Webhook,
  specs: "Our proprietary communication framework utilizes real-time translation protocols and cultural intelligence mapping. We synchronize stakeholder expectations through unified reporting channels, ensuring that technical requirements are never lost in translation during global handovers."
}, {
  title: "International Relations",
  description: "Navigating complex regulatory landscapes and fostering long-term trust with global partners.",
  icon: Shield,
  specs: "We maintain a network of local experts across 40+ jurisdictions. Our approach combines diplomatic protocol with rigorous compliance standards, securing necessary permits and building the social capital required for large-scale infrastructure and construction projects."
}, {
  title: "Resource Allocation",
  description: "Optimizing global talent and material logistics to maintain project momentum 24/7.",
  icon: Activity,
  specs: "Utilizing predictive analytics to manage supply chain volatility. We coordinate multi-national logistics hubs to ensure that critical resources—from specialized labor to raw materials—are deployed precisely when and where they are needed to prevent costly delays."
}, {
  title: "Strategic Coordination",
  description: "Centralized oversight for multi-national operations, ensuring unified project execution.",
  icon: Database,
  specs: "Our 'Orbital Perspective' dashboard provides a single source of truth for all stakeholders. We integrate disparate project data into a cohesive strategy, allowing for rapid decision-making and agile responses to shifting geopolitical or market conditions."
}];
export default function CloudSecurityIntegration() {
  return <section data-section-id="238021" id="stakeholder-synergy" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden opacity-20">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[100px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[100px] rounded-full" />
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute left-1/2 top-0 w-[1px] h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16 text-center md:text-left max-w-3xl">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Stakeholder Synergy
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default">
            Global project management excellence through strategic communication and international relationship bridging.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {technicalFeatures.map((feature, i) => <div data-index={i} key={i} className="h-full">
              <Card className="h-full flex flex-col border-border/40 bg-card/40 backdrop-blur-md hover:border-primary/40 transition-colors duration-300 relative overflow-hidden group rounded-md shadow-sm">
                {}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/5 flex items-center justify-center mb-4 text-primary">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="font-default text-xl font-semibold text-foreground">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground font-default">
                    {feature.description}
                  </CardDescription>
                </CardContent>
                
                <CardFooter className="pt-4 border-t border-border/20">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between text-primary hover:text-primary hover:bg-primary/5 font-mono text-xs uppercase tracking-widest rounded-none">
                        <span>View Strategic Specs</span>
                        <Info className="w-4 h-4 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-5 border-primary/20 shadow-2xl bg-popover/95 backdrop-blur-xl rounded-md">
                      <div className="space-y-3">
                        <h4 className="font-default font-bold text-sm text-foreground flex items-center gap-2 uppercase tracking-tight">
                          <Activity className="w-4 h-4 text-primary" />
                          Operational Insight
                        </h4>
                        <p className="text-sm text-muted-foreground font-default leading-relaxed">
                          {feature.specs}
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}